import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Save } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { useAuth, isSuperAdmin } from "@/contexts/AuthContext";

// eslint-disable-next-line react-hooks/exhaustive-deps
function useSectionForm(initial) {
  const [form, setForm] = useState(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setForm(initial), [JSON.stringify(initial)]);
  return [form, setForm];
}

export default function StoreSettings() {
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/store/settings"); setStore(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !store) {
    return <div className="p-8"><Skeleton className="h-10 w-64 mb-6" /><Skeleton className="h-96 w-full" /></div>;
  }

  // Phase 4 — Platform-level tabs (Social links + Storefront style) are
  // global settings that render across every storefront on babubaazar.in.
  // STORE_ADMIN sees only their own basic store settings; SUPER_ADMIN keeps
  // full access including Social + Storefront.
  const showPlatformTabs = isSuperAdmin(user);
  const tabs = ["profile", "gst", "bank", "hours", "delivery", "warehouse"];
  if (showPlatformTabs) tabs.push("social", "storefront");

  return (
    <div className="p-8" data-testid="store-settings-page">
      <PageHeader title="Store Settings" subtitle="Profile · GST · Bank · Hours · Delivery · Warehouse" />

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`settings-tab-${t}`} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile"><ProfileSection store={store} onSaved={load} /></TabsContent>
        <TabsContent value="gst"><GstSection store={store} onSaved={load} /></TabsContent>
        <TabsContent value="bank"><BankSection store={store} onSaved={load} /></TabsContent>
        <TabsContent value="hours"><HoursSection store={store} onSaved={load} /></TabsContent>
        <TabsContent value="delivery"><DeliverySection store={store} onSaved={load} /></TabsContent>
        <TabsContent value="warehouse"><WarehouseSection store={store} onSaved={load} /></TabsContent>
        {showPlatformTabs && (
          <>
            <TabsContent value="social"><SocialSection store={store} onSaved={load} /></TabsContent>
            <TabsContent value="storefront"><StorefrontSection store={store} onSaved={load} /></TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

function SectionCard({ title, description, onSave, saving, children, testId }) {
  return (
    <Card className="card-soft border-transparent mt-4" data-testid={testId}>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <div><CardTitle className="font-display text-xl">{title}</CardTitle>{description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}</div>
        <Button onClick={onSave} disabled={saving} className="btn-primary-glow"><Save className="w-4 h-4 mr-1" />{saving ? "Saving…" : "Save"}</Button>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// Strip empty strings so backend doesn't reject on non-null string fields
const cleanPayload = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  );

async function savePatch(section, body, onSaved) {
  try { await api.patch(`/store/settings/${section}`, body); toast.success("Settings saved"); onSaved(); }
  catch (e) { toast.error(errMsg(e)); }
}

function ProfileSection({ store, onSaved }) {
  const prof = store.settings?.profile || {};
  const [form, setForm] = useSectionForm({
    name: store.name || "",
    logo_url: prof.logo_url || "",
    tagline: prof.tagline || "",
    description: prof.description || "",
    contact_phone: store.contact?.phone || "",
    contact_email: store.contact?.email || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("profile", cleanPayload(form), onSaved); setSaving(false); };
  return (
    <SectionCard title="Store profile" onSave={save} saving={saving} testId="section-profile">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" className="mt-1" /></div>
        <div><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1" placeholder="e.g. Fresh groceries delivered in minutes" /></div>
        <div><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="settings-phone" className="mt-1" placeholder="+91 98765 43210" /></div>
        <div><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="settings-email" className="mt-1" placeholder="store@example.com" /></div>
        <div className="col-span-1 md:col-span-2">
          <ImageUpload
            value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            label="Store logo"
            hint="Square logo works best in receipts, invoices, admin sidebar and email."
            compact
            testId="settings-logo"
          />
        </div>
        <div className="col-span-1 md:col-span-2"><Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" placeholder="Brief description of your store..." /></div>
      </div>
    </SectionCard>
  );
}

function GstSection({ store, onSaved }) {
  const [form, setForm] = useSectionForm({
    gstin: store.gst?.gstin || "", legal_name: store.gst?.legal_name || "", place_of_supply: store.gst?.place_of_supply || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("gst", cleanPayload(form), onSaved); setSaving(false); };
  return (
    <SectionCard title="GST" description="Central & state tax identity" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} data-testid="settings-gstin" /></div>
        <div><Label>Legal name</Label><Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></div>
        <div><Label>Place of supply</Label><Input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} /></div>
      </div>
    </SectionCard>
  );
}

function BankSection({ store, onSaved }) {
  const [form, setForm] = useSectionForm({
    account_number: store.bank?.account_number || "",
    ifsc: store.bank?.ifsc || "",
    holder_name: store.bank?.holder_name || "",
    bank_name: store.bank?.bank_name || "",
    branch: store.bank?.branch || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("bank", cleanPayload(form), onSaved); setSaving(false); };
  return (
    <SectionCard title="Bank details" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Holder name</Label><Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} data-testid="settings-bank-holder" /></div>
        <div><Label>Account number</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
        <div><Label>IFSC</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></div>
        <div><Label>Bank name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
        <div><Label>Branch</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></div>
      </div>
    </SectionCard>
  );
}

function InvoiceSection({ store, onSaved }) {
  const inv = store.settings?.invoice || {};
  const [form, setForm] = useSectionForm({
    prefix: inv.prefix || "INV",
    next_number: inv.next_number || 1,
    footer: inv.footer || "",
    show_gst: inv.show_gst ?? true,
    show_logo: inv.show_logo ?? true,
    show_qr: inv.show_qr ?? false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await savePatch("invoice", { ...form, next_number: Number(form.next_number || 1) }, onSaved);
    setSaving(false);
  };
  return (
    <SectionCard title="Invoice numbering & layout" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Prefix</Label><Input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} data-testid="settings-invoice-prefix" /></div>
        <div><Label>Next number</Label><Input type="number" min="1" value={form.next_number} onChange={(e) => setForm({ ...form, next_number: e.target.value })} /></div>
        <div className="col-span-2"><Label>Footer text</Label><Textarea rows={3} value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} /></div>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.show_gst} onChange={(e) => setForm({ ...form, show_gst: e.target.checked })} /> Show GST split</label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.show_logo} onChange={(e) => setForm({ ...form, show_logo: e.target.checked })} /> Show logo</label>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.show_qr} onChange={(e) => setForm({ ...form, show_qr: e.target.checked })} /> Show QR / UPI code</label>
      </div>
    </SectionCard>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function HoursSection({ store, onSaved }) {
  const initial = DAYS.map((d) => {
    const found = (store.business_hours || []).find((h) => h.day === d);
    return found ? { day: d, open: found.open || "09:00", close: found.close || "22:00", is_closed: !!found.is_closed } : { day: d, open: "09:00", close: "22:00", is_closed: false };
  });
  const [hours, setHours] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("hours", { hours }, onSaved); setSaving(false); };
  return (
    <SectionCard title="Business hours" onSave={save} saving={saving}>
      <div className="space-y-2">
        {hours.map((h, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-2 text-sm font-medium">{h.day}</div>
            <div className="col-span-4"><Input type="time" value={h.open} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, open: e.target.value } : x))} disabled={h.is_closed} /></div>
            <div className="col-span-4"><Input type="time" value={h.close} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, close: e.target.value } : x))} disabled={h.is_closed} /></div>
            <label className="col-span-2 text-xs flex items-center gap-2">
              <input type="checkbox" checked={h.is_closed} onChange={(e) => setHours(hours.map((x, idx) => idx === i ? { ...x, is_closed: e.target.checked } : x))} /> Closed
            </label>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function DeliverySection({ store, onSaved }) {
  const d = store.delivery || {};
  const [form, setForm] = useSectionForm({
    radius_km: d.radius_km || 5,
    min_order_amount: d.min_order_amount || 0,
    fee_flat: d.fee_flat || 0,
    free_over: d.free_over || 0,
    prep_minutes: d.prep_minutes || 15,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const payload = {
      radius_km: Number(form.radius_km),
      min_order_amount: Number(form.min_order_amount),
      fee_flat: Number(form.fee_flat),
      free_over: Number(form.free_over),
      prep_minutes: Number(form.prep_minutes),
    };
    await savePatch("delivery", payload, onSaved);
    setSaving(false);
  };
  return (
    <SectionCard title="Delivery" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Radius (km)</Label><Input type="number" step="0.1" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} data-testid="settings-delivery-radius" /></div>
        <div><Label>Minimum order (₹)</Label><Input type="number" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} /></div>
        <div><Label>Flat fee (₹)</Label><Input type="number" value={form.fee_flat} onChange={(e) => setForm({ ...form, fee_flat: e.target.value })} /></div>
        <div><Label>Free above (₹)</Label><Input type="number" value={form.free_over} onChange={(e) => setForm({ ...form, free_over: e.target.value })} /></div>
        <div><Label>Delivery time (minutes) <span className="text-muted-foreground text-xs font-normal">— shown to customers</span></Label><Input type="number" min="5" max="180" value={form.prep_minutes} onChange={(e) => setForm({ ...form, prep_minutes: e.target.value })} data-testid="settings-delivery-eta" placeholder="e.g. 15, 30, 45, 60" /></div>
      </div>
    </SectionCard>
  );
}

function TaxSection({ store, onSaved }) {
  const t = store.settings?.tax || {};
  const [form, setForm] = useSectionForm({
    default_gst_rate: t.default_gst_rate ?? 5,
    tax_inclusive: t.tax_inclusive ?? true,
    show_gst_split: t.show_gst_split ?? true,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("tax", { ...form, default_gst_rate: Number(form.default_gst_rate) }, onSaved); setSaving(false); };
  return (
    <SectionCard title="Tax defaults" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Default GST %</Label><Input type="number" min="0" max="50" value={form.default_gst_rate} onChange={(e) => setForm({ ...form, default_gst_rate: e.target.value })} data-testid="settings-tax-default-gst" /></div>
        <div className="flex flex-col gap-2 justify-end pb-2">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.tax_inclusive} onChange={(e) => setForm({ ...form, tax_inclusive: e.target.checked })} /> Tax inclusive by default</label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.show_gst_split} onChange={(e) => setForm({ ...form, show_gst_split: e.target.checked })} /> Show CGST/SGST split on invoices</label>
        </div>
      </div>
    </SectionCard>
  );
}

function WarehouseSection({ store, onSaved }) {
  const w = store.warehouse || {};
  const [form, setForm] = useSectionForm({
    address: w.address || "",
    contact_phone: w.contact_phone || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await savePatch("warehouse", cleanPayload(form), onSaved); setSaving(false); };
  return (
    <SectionCard title="Primary warehouse" onSave={save} saving={saving}>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Warehouse address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="settings-warehouse-address" /></div>
        <div><Label>Warehouse phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
      </div>
    </SectionCard>
  );
}


// ---- Sprint 14 additions ----

async function saveCommerce(patch, existingCommerce, onSaved) {
  try {
    await api.patch("/store/settings/commerce", { ...(existingCommerce || {}), ...patch });
    toast.success("Settings saved");
    onSaved();
  } catch (e) { toast.error(errMsg(e)); }
}

// #12 Social Media Links
function SocialSection({ store, onSaved }) {
  const commerce = store.settings?.commerce || store.commerce || {};
  const social = commerce.social || {};
  const [form, setForm] = useSectionForm({
    facebook:  social.facebook  || "",
    instagram: social.instagram || "",
    youtube:   social.youtube   || "",
    whatsapp:  social.whatsapp  || "",
    telegram:  social.telegram  || "",
    twitter:   social.twitter   || "",
    linkedin:  social.linkedin  || "",
    website:   social.website   || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await saveCommerce({ social: cleanPayload(form) }, commerce, onSaved); setSaving(false); };
  return (
    <SectionCard title="Social media" description="Shown in the footer and contact page. Leave blank to hide an icon." onSave={save} saving={saving} testId="section-social">
      <div className="grid grid-cols-2 gap-3">
        {[
          ["facebook",  "Facebook",  "https://facebook.com/…"],
          ["instagram", "Instagram", "https://instagram.com/…"],
          ["youtube",   "YouTube",   "https://youtube.com/@…"],
          ["whatsapp",  "WhatsApp",  "https://wa.me/91…"],
          ["telegram",  "Telegram",  "https://t.me/…"],
          ["twitter",   "Twitter / X", "https://x.com/…"],
          ["linkedin",  "LinkedIn",  "https://linkedin.com/company/…"],
          ["website",   "Website",   "https://your-site.com"],
        ].map(([key, label, ph]) => (
          <div key={key}>
            <Label>{label}</Label>
            <Input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph} data-testid={`settings-social-${key}`} />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// #13 + #14 Storefront style
function StorefrontSection({ store, onSaved }) {
  const commerce = store.settings?.commerce || store.commerce || {};
  const style = commerce.storefront || {};
  const [form, setForm] = useSectionForm({
    category_style:      style.category_style      || "circle",       // circle | rounded | square
    category_font:       style.category_font       || "default",      // default | display | semibold | condensed
    section_bg:          style.section_bg          || "",             // hex, empty=default
    card_bg:             style.card_bg             || "",
    section_padding:     style.section_padding     || "compact",      // compact | comfy | roomy
    border_radius:       style.border_radius       || "16",           // px
    section_title_color: style.section_title_color || "",
    button_color:        style.button_color        || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); await saveCommerce({ storefront: cleanPayload(form) }, commerce, onSaved); setSaving(false); };
  const pick = (key, value) => setForm({ ...form, [key]: value });
  return (
    <SectionCard title="Storefront style" description="Controls category card shape, section padding, colors and corners across the customer site." onSave={save} saving={saving} testId="section-storefront">
      <div className="space-y-6">
        <div>
          <Label className="mb-2 block">Category card style</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "circle",  label: "Circle",   sample: "rounded-full" },
              { key: "rounded", label: "Rounded",  sample: "rounded-2xl" },
              { key: "square",  label: "Square",   sample: "rounded-md" },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => pick("category_style", o.key)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition inline-flex items-center gap-2 ${form.category_style === o.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                data-testid={`settings-catstyle-${o.key}`}
              >
                <span className={`w-6 h-6 bg-secondary block ${o.sample}`} />
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Category label font</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "default",   label: "Default",   sample: "font-medium" },
              { key: "display",   label: "Display",   sample: "font-display italic" },
              { key: "semibold",  label: "Bold",      sample: "font-bold" },
              { key: "condensed", label: "Condensed", sample: "font-semibold tracking-tighter" },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => pick("category_font", o.key)}
                className={`px-4 py-2 rounded-lg border text-sm transition inline-flex items-center gap-2 ${form.category_font === o.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                data-testid={`settings-catfont-${o.key}`}
              >
                <span className={`text-xs ${o.sample}`}>Aa</span>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Section padding</Label>
          <div className="flex flex-wrap gap-2">
            {["compact", "comfy", "roomy"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => pick("section_padding", k)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium capitalize transition ${form.section_padding === k ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                data-testid={`settings-padding-${k}`}
              >{k}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <Label>Card border radius (px)</Label>
            <Input type="number" min="0" max="40" value={form.border_radius} onChange={(e) => pick("border_radius", e.target.value)} data-testid="settings-radius" />
          </div>
          <div>
            <Label>Section background</Label>
            <Input type="color" value={form.section_bg || "#faf7f4"} onChange={(e) => pick("section_bg", e.target.value)} data-testid="settings-section-bg" />
          </div>
          <div>
            <Label>Card background</Label>
            <Input type="color" value={form.card_bg || "#ffffff"} onChange={(e) => pick("card_bg", e.target.value)} data-testid="settings-card-bg" />
          </div>
          <div>
            <Label>Section title color</Label>
            <Input type="color" value={form.section_title_color || "#141414"} onChange={(e) => pick("section_title_color", e.target.value)} data-testid="settings-title-color" />
          </div>
          <div>
            <Label>Primary button color</Label>
            <Input type="color" value={form.button_color || "#dc1f26"} onChange={(e) => pick("button_color", e.target.value)} data-testid="settings-btn-color" />
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
